// LogoutPage.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import { CheckCircle, Error, ExitToApp } from '@mui/icons-material';

const LogoutPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [logoutStatus, setLogoutStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const performLogout = async () => {
      try {
        // Se l'utente è ancora autenticato, esegui il logout
        if (auth.user) {
          await auth.signoutSilent();
          await auth.removeUser();
        }
        
        // Pulisci eventuali dati locali
        localStorage.clear();
        sessionStorage.clear();
        
        // Rimuovi tutti i cookie (se necessario)
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
        });

        setLogoutStatus('success');
        
        // Redirect automatico dopo 3 secondi
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        
      } catch (error) {
        // console.error('Errore durante il logout:', error);
        // setErrorMessage(error.message || 'Errore sconosciuto durante il logout');
        setLogoutStatus('error');
      }
    };

    performLogout();
  }, [auth, navigate]);

  const handleReturnToLogin = () => {
    navigate('/login');
  };

  const handleRetry = () => {
    setLogoutStatus('processing');
    window.location.reload();
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="grey.50"
      p={2}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        {logoutStatus === 'processing' && (
          <>
            <CircularProgress size={60} color="primary" sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Logout in corso...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Stiamo disconnettendo il tuo account in modo sicuro.
            </Typography>
          </>
        )}

        {logoutStatus === 'success' && (
          <>
            <CheckCircle 
              sx={{ 
                fontSize: 60, 
                color: 'success.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" gutterBottom color="success.main">
              Logout completato
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Sei stato disconnesso con successo. Verrai reindirizzato alla pagina di login tra poco.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ExitToApp />}
              onClick={handleReturnToLogin}
              sx={{ mt: 2 }}
            >
              Vai al Login
            </Button>
          </>
        )}

        {logoutStatus === 'error' && (
          <>
            <Error 
              sx={{ 
                fontSize: 60, 
                color: 'error.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h5" gutterBottom color="error.main">
              Errore durante il logout
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {errorMessage || 'Si è verificato un errore durante la disconnessione.'}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={handleRetry}
                color="error"
              >
                Riprova
              </Button>
              <Button
                variant="contained"
                onClick={handleReturnToLogin}
              >
                Vai al Login
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default LogoutPage;